import { Proposta } from './../../../../shared/models/proposta';
import { Cliente } from './../../../../shared/models/cliente';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { take, finalize, takeWhile } from 'rxjs/operators';

import { NewModalMessageComponent } from '../../../../../shared/modais/new-modal-message/new-modal-message.component';
import { TipoProdutoEnum } from '../../../../shared/enums/tipo-produto.enum';
import { PropostaStateService } from '../../../../shared/states/proposta-state.service';
import { CreditoPessoalService } from '../../../services/cp.service';
import { TipoTelefoneEnum } from '../../../../../shared/enums/tipo-telefone.enum';

@Component({
  selector: 'app-telefone-base',
  templateUrl: './telefone-base.component.html',
  styleUrls: ['./telefone-base.component.css']
})
export class TelefoneBaseComponent implements OnInit, OnDestroy {
  @Input() needsConfirmation: boolean;
  @Input() proposta: Proposta;
  @Input() cliente: Cliente;
  @Input() isRemovable: boolean;
  @Input() index?: number = -1;
  @Input() detalhesClienteForm: FormGroup;
  @Input() tipoProduto: TipoProdutoEnum;
  @Input() isConfirmado: boolean = false;
  @Input() adicional: boolean = false;
  @Input() readOnly: boolean = false;
  @Output() telefoneDestroyEventEmitter: EventEmitter<number> = new EventEmitter();
  @Output() confirmacaoEmitter: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('smsModal', { static: true }) smsModal: NewModalMessageComponent;

  telefoneForm: FormGroup;
  smsForm: FormGroup;

  readonly TipoTelefoneEnum = TipoTelefoneEnum;
  isResidencial: boolean = false;
  showLoader: boolean = false;
  isAlive: boolean = true;

  constructor(
    private fb: FormBuilder,
    private state: PropostaStateService,
    private cpService: CreditoPessoalService
  ) { }

  ngOnInit() {
    this.initForms();
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  private initForms(): void {
    let tipoDefault = this.tipoCelular;
    if (this.index > 0) {
      const tipoAnterior = this.detalhesClienteForm.get('telefoneForm_0').get('tipoTelefone');
      tipoAnterior.disable({ emitEvent: false });

      if (tipoAnterior.value === tipoDefault) {
        tipoDefault = TipoTelefoneEnum.RESIDENCIAL;
        this.isResidencial = true;
      }
    }

    this.telefoneForm = this.fb.group({
      tipoTelefone: [tipoDefault, Validators.required],
      telefone: ['', this.getTelefoneValidators()]
    });

    if (this.index > 0) {
      this.tipoTelefone.disable();
    }

    if (this.isConfirmado || this.readOnly) {
      this.telefoneForm.disable();
    }

    if (!this.isRemovable) {
      this.smsForm = this.fb.group({
        codigoConfirmacao: ['', Validators.required]
      });
    }

    this.detalhesClienteForm.addControl(`telefoneForm_${this.index}`, this.telefoneForm);

    this.tipoTelefone
      .valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => this.onChangePhoneType(this.tipoTelefone.value));
  }

  sendConfirmacaoSms(): void {
    if (this.telefoneForm.invalid) {
      this.handleError('Telefone inválido!');
      return;
    }

    this.showLoader = true;
    this.cpService.enviarSms(this.cliente.id, {
      ddd: this.telefone.value.replace(/[^0-9]/g, '').substring(0, 2),
      telefone: this.telefone.value.replace(/[^0-9]/g, '').substring(2),
      proposta: this.proposta.id
    }).pipe(
      finalize(() => this.showLoader = false),
      take(1)
    )
      .subscribe(
        res => this.handleEnvioConfirmacaoSms(res),
        error => this.handleEnvioConfirmacaoSmsError(error));
  }

  private handleEnvioConfirmacaoSms(res: any): void {
    if (res.status === 'SUCCESS') {
      return this.smsModal.showModal({
        titulo: 'Sucesso',
        mensagem: 'Código de confirmação enviado com sucesso. Sua mensagem chegará em instantes.'
      });
    }
    this.handleEnvioConfirmacaoSmsError(res);
  }
  private handleEnvioConfirmacaoSmsError(error: any) {
    return this.handleError('Houve uma falha no envio do SMS de confirmação.');
  }

  private handleError(error: string) {
    return this.smsModal.showModal({
      isTituloVermelho: true,
      titulo: 'Falha',
      mensagem: error
    });
  }

  handleConfirmacao(): void {
    if (this.codigoConfirmacao.valid) {
      this.showLoader = true;

      this.cpService
        .verificarToken(this.cliente.id, this.codigoConfirmacao.value)
        .pipe(
          finalize(() => this.showLoader = false),
          takeWhile(() => this.isAlive)
        )
        .subscribe(
          res => this.handleConfirmacaoSuccess(res),
          error => this.handleConfirmacaoError(error)
        );
    }
  }

  private handleConfirmacaoSuccess(res: any): any {
    if (res.status === 'SUCCESS') {
      this.isConfirmado = true;

      const transients = this.cliente.transients || {};
      this.cliente.transients = { ...transients, isConfirmadoTelefone: true };
      this.confirmacaoEmitter.next(true);
      this.telefone.disable();
    } else {
      this.handleConfirmacaoError(res);
    }
  }

  private handleConfirmacaoError(error: any): void {
    this.smsModal.showModal({
      titulo: 'Falha',
      mensagem: 'Código de verificação inválido. Por favor, tente novamente.'
    });
  }

  selfDestruct(): void {
    this.detalhesClienteForm.removeControl(`telefoneForm_${this.index}`);
    this.telefoneDestroyEventEmitter.emit(this.index);

    if (this.index > 0) {
      const tipoAnterior = this.detalhesClienteForm.get('telefoneForm_0').get('tipoTelefone');
      tipoAnterior.enable({ emitEvent: false });
    }

  }

  onChangePhoneType(phoneType): void {
    this.telefoneForm.get('telefone').setValue('');
    this.isResidencial = phoneType === TipoTelefoneEnum.RESIDENCIAL ? true : false;
  }

  getTelefoneValidators(): ValidatorFn[] {
    if (this.isResidencial) {
      return [Validators.required, Validators.minLength(13), Validators.maxLength(14)];
    }
    return [Validators.required, Validators.minLength(14), Validators.maxLength(15)];
  }

  get tipoTelefone(): AbstractControl {
    return this.telefoneForm.get('tipoTelefone');
  }

  get telefone(): AbstractControl {
    return this.telefoneForm.get('telefone');
  }

  get codigoConfirmacao(): AbstractControl {
    return this.smsForm.get('codigoConfirmacao');
  }

  get tipoCelular(): string {
    if (this.adicional) {
      return TipoTelefoneEnum.CELULAR_ADICIONAL;
    }
    return TipoTelefoneEnum.CELULAR;
  }
}
